import * as Sentry from "@sentry/nextjs";


/**
 * Initialize Sentry for the running environment.
 * @param {import("@sentry/nextjs").BrowserOptions | import("@sentry/nextjs").NodeOptions} options
 */
// @ts-expect-error: options is typed via JSDoc for editors; keep runtime signature JS-safe for bundlers
export function initSentry(options) {
  // Preserve any user-supplied beforeSend AND beforeBreadcrumb
  const userBeforeSend = options && options.beforeSend;
  const userBeforeBreadcrumb = options && options.beforeBreadcrumb;

  // Use plain JS parameters so this file can be parsed without a TS loader,
  // but annotate them here to satisfy TypeScript (avoid implicit any).
  /**
   * @param {unknown} event
   * @param {unknown} [hint]
   * @returns {unknown | null}
   */
  // @ts-expect-error: JSDoc types not recognized, suppress implicit any for webpack compatibility
  function beforeSend(event, hint) {
    // If the user supplied a beforeSend (e.g. client-level advanced filter),
    // run it first. If it returns null, respect that decision and drop the event.
    if (typeof userBeforeSend === "function") {
      try {
        // Call user beforeSend with original args. If it returns null, stop.
        const res = userBeforeSend(event, hint);
        if (res === null) return null;
        // If user returned a different event object, prefer that
        if (res !== undefined) {
          event = res;
        }
      } catch (_err) {
        // If user beforeSend throws, continue to package-level filter as a fallback.
      }
    }

    // Package-level fallback filter: keep the previous simple Safari unhandledrejection drop.
    // This ensures server/edge usage (or clients without advanced filter) still benefit.
    try {
      // Minimal runtime shape checks (no TypeScript "type" declarations so file stays JS-parsable).
      // ExceptionValue ~ { mechanism?: { type?: string }; stacktrace?: { frames?: unknown[] } }
      // ExceptionContainer ~ { exception?: { values?: ExceptionValue[] } }

      const ev = event; // treat as generic object at runtime
      const ex =
        ev && ev.exception && ev.exception.values && ev.exception.values[0];
      const mechanismType = ex && ex.mechanism && ex.mechanism.type;
      const hasFrames = !!(
        ex &&
        ex.stacktrace &&
        Array.isArray(ex.stacktrace.frames) &&
        ex.stacktrace.frames.length
      );
      if (mechanismType === "onunhandledrejection" && !hasFrames) {
        return null;
      }
    } catch (_err) {
      // If anything goes wrong inspecting the event, continue and return the event.
    }

    return event;
  }

  /**
   * Shared beforeBreadcrumb that enriches resource errors
   * @param {unknown} breadcrumb
   * @param {unknown} [hint]
   * @returns {unknown}
   */
  // @ts-expect-error: JSDoc types for webpack compatibility
  function beforeBreadcrumb(breadcrumb, hint) {
    // Run user breadcrumb enrichment first if provided
    let bc = breadcrumb;
    if (typeof userBeforeBreadcrumb === "function") {
      try {
        const res = userBeforeBreadcrumb(breadcrumb, hint);
        if (res !== undefined) bc = res;
      } catch (_err) {
        // Continue with package-level enrichment
      }
    }

    // Package-level enrichment: capture resource load errors
    try {
      const evt = hint && hint.event;
      if (evt && evt.type === "error" && evt.target) {
        const t = evt.target;
        const resourceUrl = t.src || t.href;
        if (resourceUrl && bc && typeof bc === "object") {
          bc.data = {
            ...(bc.data || {}),
            resourceUrl,
            tagName: (t.tagName || "").toLowerCase(),
          };
        }
      }
    } catch (_err) {
      // Never block breadcrumb
    }

    return bc;
  }

  if (process.env.NODE_ENV === "production" && options.dsn) {
    console.log("Initializing Sentry in production with DSN");

    // Add Elevar/GTM Suite ignores and some browser-extension noise; merge with user options if present
    const defaultIgnoreErrors = [
      // Elevar/GTM Suite errors
      /Failed to fetch.*getelevar\.com/,
      /Failed to fetch dynamically imported module.*getelevar\.com/,
      /Cannot read properties of undefined.*script_src_custom_pages/,
      "Importing a module script failed",

      // Browser extension noise
      "Invalid call to runtime.sendMessage",
      "Failed to connect to MetaMask",
    ];

    const defaultDenyUrls = [
      // Ignore errors from Elevar's domains
      /getelevar\.com/i,
      /shopify-gtm-suite\.getelevar\.com/i,
    ];

    Sentry.init({
      ...options,
      // merge any user-provided lists with our defaults (user entries first)
      ignoreErrors: [
        ...(options && Array.isArray(options.ignoreErrors) ? options.ignoreErrors : []),
        ...defaultIgnoreErrors,
      ],
      denyUrls: [
        ...(options && Array.isArray(options.denyUrls) ? options.denyUrls : []),
        ...defaultDenyUrls,
      ],
      beforeSend,
      beforeBreadcrumb,
      environment: process.env.CONTEXT || process.env.NODE_ENV,
      release: process.env.COMMIT_REF,
    });

    // Wire up router transition tracking for client-side only
    // The Integration returned by browserTracingIntegration does not have startNavigationSpan.
    // If you need to start navigation spans, use Sentry.startSpan or similar API directly.
    return {
      onRequestError: Sentry.captureRequestError,
      onRouterTransitionStart: () => undefined,
    };
  } else {
    const reason =
      process.env.NODE_ENV !== "production"
        ? "development mode"
        : "missing DSN";
    console.log(`Sentry not initialized: ${reason}`);
    return {
      onRequestError: () => undefined,
      onRouterTransitionStart: () => undefined,
    };
  }
}